export type PromiseTtlCacheOptions = {
  ttlMs: number;
  /** Injectable clock for deterministic tests. */
  now?: () => number;
};

export type PromiseTtlCache<Key, Value> = {
  get: (
    key: Key,
    load: () => Promise<Value>,
    shouldCache?: (value: Value) => boolean,
  ) => Promise<Value>;
  delete: (key: Key) => boolean;
  clear: () => void;
};

/**
 * A small in-memory TTL cache for asynchronous reads.
 *
 * The in-flight promise is cached immediately, so concurrent callers for the
 * same key share one load. Rejections and values rejected by `shouldCache` are
 * evicted, allowing the next caller to retry.
 */
export const createPromiseTtlCache = <Key, Value>({
  ttlMs,
  now = Date.now,
}: PromiseTtlCacheOptions): PromiseTtlCache<Key, Value> => {
  if (!Number.isFinite(ttlMs) || ttlMs <= 0) {
    throw new Error("Promise cache TTL must be a positive finite number.");
  }

  const entries = new Map<
    Key,
    { expiresAt: number; value: Promise<Value> }
  >();

  const get = (
    key: Key,
    load: () => Promise<Value>,
    shouldCache: (value: Value) => boolean = () => true,
  ): Promise<Value> => {
    const loadedAt = now();
    const hit = entries.get(key);
    if (hit && hit.expiresAt > loadedAt) return hit.value;

    let value: Promise<Value>;
    const evictIfCurrent = () => {
      if (entries.get(key)?.value === value) entries.delete(key);
    };

    let loaded: Promise<Value>;
    try {
      loaded = Promise.resolve(load());
    } catch (error) {
      loaded = Promise.reject(error);
    }

    value = loaded
      .then((result) => {
        if (!shouldCache(result)) evictIfCurrent();
        return result;
      })
      .catch((error: unknown) => {
        evictIfCurrent();
        throw error;
      });

    entries.set(key, { expiresAt: loadedAt + ttlMs, value });
    return value;
  };

  return {
    get,
    delete: (key) => entries.delete(key),
    clear: () => entries.clear(),
  };
};
